/**
 * SSE (`GET /v1/events`) -> invalidacija TanStack upita + toast + obavještenja.
 * Status veze (`connected` / `reconnecting`) prikazuje pilula u headeru.
 */
import type { RealtimeEvent, RealtimeEventType } from '@sm/api';
import { eventsUrl } from '@sm/api';
import type { QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { env } from '@/env';

import { qk } from './queries';

export type RealtimeStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface EventSourceLike {
  onopen: ((ev: Event) => void) | null;
  onerror: ((ev: Event) => void) | null;
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void;
  close(): void;
  readonly readyState: number;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

const EVENT_TYPES: RealtimeEventType[] = [
  'project.updated',
  'measurement.decided',
  'production.updated',
  'notification.created',
  'device.updated',
];

let status: RealtimeStatus = 'connecting';
let lastEventAt: number | null = null;
const listeners = new Set<() => void>();
const setStatus = (s: RealtimeStatus) => {
  status = s;
  listeners.forEach((l) => l());
};

export function useRealtimeStatus(): { status: RealtimeStatus; lastEventAt: number | null } {
  const s = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => status,
    () => status,
  );
  return { status: s, lastEventAt };
}

/** Koji upiti se invalidiraju za koji događaj (testirano u realtime.test). */
export function queryKeysForEvent(ev: RealtimeEvent): readonly (readonly unknown[])[] {
  switch (ev.type) {
    case 'project.updated':
      return [['projects'], qk.dashboard, ['measurements'], ['photos']];
    case 'measurement.decided':
      return [['measurements'], ['projects'], qk.dashboard];
    case 'production.updated':
      return [['production'], qk.dashboard, ['projects']];
    case 'notification.created':
      // Brisanje naloga / firme mijenja listu korisnika i `Me.tenant.deletionScheduledAt`.
      if (ev.notification?.type === 'account_deletion_requested')
        return [qk.notifications, ['users']];
      if (ev.notification?.type === 'company_deletion_scheduled')
        return [qk.notifications, ['users'], qk.me];
      if (ev.notification?.type === 'support_report_submitted')
        return [qk.notifications, ['support'], ['devices']];
      if (ev.notification?.type === 'device_critical') return [qk.notifications, ['devices']];
      return [qk.notifications];
    case 'device.updated':
      return [['devices']];
    default:
      return [];
  }
}

export interface RealtimeOptions {
  queryClient: QueryClient;
  onNotification?: (ev: RealtimeEvent) => void;
  factory?: EventSourceFactory;
  /** Početno kašnjenje ponovnog spajanja (ms). */
  backoffMs?: number;
}

const defaultFactory: EventSourceFactory = (url) =>
  new EventSource(url, { withCredentials: true }) as unknown as EventSourceLike;

let mockFactory: EventSourceFactory | null = null;
/** Mock režim (MSW ne pokriva SSE) registruje vlastiti izvor događaja. */
export function setMockEventSourceFactory(f: EventSourceFactory | null): void {
  mockFactory = f;
}

/** Pokreće vezu i vraća funkciju za zatvaranje. */
export function connectRealtime(opts: RealtimeOptions): () => void {
  const factory = opts.factory ?? mockFactory ?? defaultFactory;
  let es: EventSourceLike | null = null;
  let closed = false;
  let retry = opts.backoffMs ?? 1000;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const handle = (msg: MessageEvent) => {
    let ev: RealtimeEvent;
    try {
      ev = JSON.parse(String(msg.data)) as RealtimeEvent;
    } catch {
      return;
    }
    lastEventAt = Date.now();
    for (const key of queryKeysForEvent(ev)) {
      void opts.queryClient.invalidateQueries({ queryKey: key as unknown[] });
    }
    if (ev.type === 'notification.created') opts.onNotification?.(ev);
    listeners.forEach((l) => l());
  };

  const open = () => {
    if (closed) return;
    try {
      es = factory(eventsUrl(env.apiUrl));
    } catch {
      setStatus('offline');
      return;
    }
    es.onopen = () => {
      lastEventAt = Date.now();
      retry = opts.backoffMs ?? 1000;
      setStatus('connected');
    };
    es.onerror = () => {
      if (closed) return;
      setStatus('reconnecting');
      // EventSource se sam ponovo spaja; ako je zatvoren (CLOSED = 2), otvaramo novi uz backoff.
      if (es && es.readyState === 2) {
        es.close();
        timer = setTimeout(open, retry);
        retry = Math.min(retry * 2, 30_000);
      }
    };
    for (const type of EVENT_TYPES) es.addEventListener(type, handle);
    es.addEventListener('message', handle);
  };

  setStatus('connecting');
  open();
  const onOffline = () => setStatus('offline');
  const onOnline = () => setStatus('reconnecting');
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);

  return () => {
    closed = true;
    clearTimeout(timer);
    es?.close();
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('online', onOnline);
  };
}
